import React, { useState, useEffect } from "react";
import toast, { Toaster } from "react-hot-toast";
import { requestForToken, messaging } from "./firebase";
import io from "socket.io-client";
import ApiConfig from "src/apiconfig/apiconfig";
import { onMessage } from "firebase/messaging";

import { makeStyles, Box, Typography } from "@material-ui/core";
import Snackbar from "@material-ui/core/Snackbar";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import DoneIcon from "@material-ui/icons/Done";
import { useHistory } from "react-router-dom";
import { localeFunction } from "src/utils/index";

import NitificationPopUp from "src/component/NotificationAlertPopUp";

const useStyles = makeStyles((theme) => ({
  acceptBtn: {
    fontSize: "14px",
    color: "green",
  },
  rejectBtn: {
    fontSize: "14px",
    color: "red",
  },
}));

const Notification = () => {
  const [open, setOpen] = useState(false);
  const [notification, setNotification] = useState({ title: "", body: "" });
  const [notificationData, setNotificationData] = useState();

  // Request token on mount only if permission is already granted
  useEffect(() => {
    if (Notification.permission === "granted") {
      requestForToken();
    }

    // Register Service Worker for Firebase Messaging
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker
        .register("/firebase-messaging-sw.js")
        .then((registration) => {
          console.log("Service worker registered:", registration);
        })
        .catch((err) => {
          console.log("Service worker registration failed:", err);
        });
    }
  }, []);

  // Set up continuous listener for Firebase messages
  useEffect(() => {
    if (!messaging) {
      console.warn("Firebase messaging not available");
      return;
    }

    console.log("🔔 Setting up Firebase message listener...");

    // Set up continuous listener using Firebase's onMessage directly
    const unsubscribe = onMessage(messaging, (payload) => {
      console.log("🚀 ~ Firebase message received:", payload);
      console.log("🚀 ~ Notification data:", payload?.notification);
      console.log("🚀 ~ Data payload:", payload?.data);

      // Extract notification data
      const notificationObj = {
        title: payload?.notification?.title || payload?.data?.title || "Voice Calling...",
        body: payload?.notification?.body || payload?.data?.body || "Incoming call...",
      };

      // Extract call data
      const dataObj = {
        callType: payload?.data?.callType || "Voice",
        channelId: payload?.data?.channelId || payload?.data?.channel,
        token: payload?.data?.token,
        name: payload?.data?.name,
        profilePic: payload?.data?.profilePic,
        ...payload?.data,
      };

      console.log("🔔 Setting notification:", notificationObj);
      console.log("🔔 Setting notification data:", dataObj);
      console.log("✅ Incoming call notification received!");

      // Update state
      setNotification(notificationObj);
      setNotificationData(dataObj);

      // Open the notification popup
      console.log("🔔 Opening notification popup...");
      setOpen(true);

      // Log notification status
      console.log("📱 Notification Status: ✅ Active - Showing popup to user");
    });

    // Listen for messages forwarded by the service worker (background messages)
    if (navigator?.serviceWorker) {
      navigator.serviceWorker.addEventListener("message", (event) => {
        if (event?.data?.type === "FIREBASE_BG_MESSAGE") {
          const payload = event.data.payload || {};
          console.log("📩 SW forwarded background message:", payload);
          const notificationObj = {
            title: payload?.notification?.title || payload?.data?.title || "Voice Calling...",
            body: payload?.notification?.body || payload?.data?.body || "Incoming call...",
          };
          const dataObj = {
            callType: payload?.data?.callType || "Voice",
            channelId: payload?.data?.channelId || payload?.data?.channel,
            token: payload?.data?.token,
            name: payload?.data?.name,
            profilePic: payload?.data?.profilePic,
            ...payload?.data,
          };
          setNotification(notificationObj);
          setNotificationData(dataObj);
          setOpen(true);
          console.log("📱 Notification Status: ✅ Active (from SW)");
        }
      });
    }

    console.log("✅ Firebase listener set up successfully");

    // Cleanup function
    return () => {
      console.log("🔔 Notification listener cleanup");
    };
  }, []); // Empty dependency array - run only once on mount

  // Setup Socket.io connection for incoming calls (like Website)
  useEffect(() => {
    let socket = null;

    const getSocketBaseUrl = () => {
      // Try explicit socketUrl if present (Website-style)
      const explicit = ApiConfig?.socketUrl;
      if (explicit) return explicit;
      // Derive base from any API endpoint by stripping /api/v1
      const sample = ApiConfig?.AdminLogin || "";
      const idx = sample.indexOf("/api/v1");
      if (idx > -1) return sample.substring(0, idx);
      // Fallback to current origin
      return window.location.origin;
    };

    const connectSocket = () => {
      const userId = localStorage.getItem("userId");
      const adminToken = localStorage.getItem("adminToken");
      if (!userId || !adminToken) {
        console.log("⚠️ Admin not logged in, skipping Socket.io connection");
        return null;
      }

      const socketUrl = getSocketBaseUrl();
      console.log("🔌 Connecting to Socket.io:", socketUrl, "for admin:", userId);

      socket = io(socketUrl, {
        transports: ["polling"], // Avoid WS conflicts, mirror Website behavior
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionAttempts: 5,
        forceNew: false,
        upgrade: false,
      });

      socket.on("connect", () => {
        console.log("✅ Socket.io connected:", socket.id);
        socket.emit("userConnected", {
          userId: userId,
          token: adminToken,
        });
      });

      socket.on("incomingCall", (callData) => {
        console.log("📞 Incoming call via WebSocket (Admin):", callData);

        // Expect shape similar to Website: { notification, callData, callType, sender }
        if (callData && (callData.notification || callData.callData || callData.data)) {
          const notif = callData.notification || {};
          const payload = callData.callData || callData.data || {};

          setNotification({
            title: notif.title || "Incoming Call",
            body: notif.body || "Someone is calling...",
          });
          setNotificationData({
            ...payload,
            name: callData.sender?.userName || payload.name,
            profilePic: callData.sender?.profilePic || payload.profilePic,
            callType: callData.callType || payload.callType || "Voice",
          });
          setOpen(true);
        }
      });

      socket.on("disconnect", () => {
        console.log("❌ Socket.io disconnected (Admin)");
      });
      socket.on("connect_error", (error) => {
        console.error("❌ Socket.io connection error (Admin):", error);
      });

      return socket;
    };

    // Connect now if logged in
    socket = connectSocket();

    // Reconnect on storage changes (login from same/other tab)
    const handleStorageChange = () => {
      const userId = localStorage.getItem("userId");
      const adminToken = localStorage.getItem("adminToken");
      if (userId && adminToken && (!socket || !socket.connected)) {
        if (socket) socket.disconnect();
        socket = connectSocket();
      }
    };
    window.addEventListener("storage", handleStorageChange);

    // Periodic quick checks for initial login state
    let count = 0;
    const maxChecks = 5;
    const interval = setInterval(() => {
      count += 1;
      const userId = localStorage.getItem("userId");
      const adminToken = localStorage.getItem("adminToken");
      if (userId && adminToken && (!socket || !socket.connected)) {
        if (socket) socket.disconnect();
        socket = connectSocket();
      }
      if (count >= maxChecks || (socket && socket.connected)) {
        clearInterval(interval);
      }
    }, 2000);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(interval);
      if (socket) socket.disconnect();
    };
  }, []);

  // Close handler
  const handleClose = () => {
    setOpen(false);
    // Reset notification after a delay to allow animation
    setTimeout(() => {
      setNotification({ title: "", body: "" });
      setNotificationData(null);
    }, 300);
  };

  return (
    <NitificationPopUp
      data={notificationData}
      notification={notification}
      open={open}
      setOpen={handleClose}
    />
  );
};

export default Notification;
